import { useDataContext } from "@/context/DataContext";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePublicDoctors } from "@/hooks/useCMSDoctors";
import { getLocalized, isRTL } from "@/utils/localization";

interface StepDoctorProps {
  serviceId: string;
  selectedDoctor: string;
  visitType: string;
  onSelectDoctor: (id: string) => void;
  onSelectVisitType: (type: string) => void;
  onNext: () => void;
  onBack: () => void;
}

export default function StepDoctor({
  serviceId,
  selectedDoctor,
  visitType,
  onSelectDoctor,
  onSelectVisitType,
  onNext,
  onBack,
}: StepDoctorProps) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const rtl = isRTL(currentLang);
  const { services: servicesData } = useDataContext();
  const { doctors: doctorsDetailed } = usePublicDoctors();

  const visitTypes = [
    { id: "first",       label: t("booking.step2.first_visit"),  icon: "ri-user-add-line",         desc: t("booking.step2.first_visit_desc") },
    { id: "followup",    label: t("booking.step2.followup"),       icon: "ri-refresh-line",           desc: t("booking.step2.followup_desc") },
    { id: "consultation",label: t("booking.step2.consultation"),      icon: "ri-question-answer-line",   desc: t("booking.step2.consultation_desc") },
  ];

  const selectedServiceIds = serviceId ? serviceId.split(",") : [];
  const selectedServices = servicesData.filter((s) => selectedServiceIds.includes(s.id));

  const doctorMapping = selectedServices.map((service, index) => {
    const ids = selectedDoctor ? selectedDoctor.split(",") : [];
    return ids[index] || "";
  });

  const handleSelectDoctorForService = (serviceIndex: number, docId: string) => {
    const ids = [...doctorMapping];
    ids[serviceIndex] = docId;
    onSelectDoctor(ids.join(","));
  };

  const canProceed = doctorMapping.length > 0 && doctorMapping.every((id) => id !== "");

  return (
    <div>
      {/* Service context */}
      {selectedServices.length > 0 && (
        <div className="flex items-center gap-2 bg-brand-cream-50 border border-brand-cream-200 rounded-xl px-4 py-2.5 mb-5 w-full">
          <i className="ri-stethoscope-line text-brand-forest-600 text-sm"></i>
          <span className="text-xs font-semibold text-brand-forest-700 truncate max-w-[80%]">
            {t("booking.step2.selected_services")} {selectedServices.map(s => getLocalized(currentLang, s.name, s.name_en).split("—")[0].trim()).join(rtl ? " ، " : ", ")}
          </span>
          <button onClick={onBack} className={`${rtl ? "mr-auto" : "ml-auto"} text-xs text-brand-forest-500 font-semibold hover:underline cursor-pointer whitespace-nowrap`}>
            {t("booking.step2.change")}
          </button>
        </div>
      )}

      <div className="mb-5">
        <h2 className="text-xl font-black text-gray-900 mb-1">{t("booking.step2.choose_docs")}</h2>
        <p className="text-gray-400 text-sm">{t("booking.step2.choose_docs_desc")}</p>
      </div>

      {/* Visit Type */}
      <div className="mb-6">
        <p className="text-sm font-semibold text-gray-700 mb-3">{t("booking.step2.choose_visit_type")}</p>
        <div className="grid grid-cols-3 gap-2">
          {visitTypes.map((vt) => (
            <button
              key={vt.id}
              onClick={() => onSelectVisitType(vt.id)}
              className={`p-3 rounded-xl border-2 text-center transition-all duration-200 cursor-pointer ${
                visitType === vt.id
                  ? "border-brand-forest-500 bg-brand-cream-50"
                  : "border-gray-100 bg-white hover:border-brand-cream-300"
              }`}
            >
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1.5 rounded-lg bg-brand-cream-100">
                <i className={`${vt.icon} text-brand-forest-600 text-sm`}></i>
              </div>
              <p className={`text-xs font-bold ${visitType === vt.id ? "text-brand-forest-700" : "text-gray-700"}`}>{vt.label}</p>
              <p className="text-[10px] text-gray-400">{vt.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Services and Doctors selection list */}
      <div className="space-y-6">
        {selectedServices.map((service, index) => {
          const serviceDocNames = service.doctors.map((d) => d.name);
          const serviceDoctors = doctorsDetailed.filter((d) => serviceDocNames.includes(d.name));
          const docsToDisplay = serviceDoctors.length > 0 ? serviceDoctors : doctorsDetailed;
          const currentDocId = doctorMapping[index] || "";
          const svcName = getLocalized(currentLang, service.name, service.name_en);
          const svcCategory = getLocalized(currentLang, service.category, service.category_en);

          return (
            <div key={service.id} className="border border-brand-cream-200 rounded-2xl p-4 bg-white shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center bg-brand-cream-100 rounded-lg">
                  <i className={`${service.icon} text-brand-forest-600`}></i>
                </div>
                <div>
                  <h4 className="font-bold text-sm text-gray-900">{svcName.split("—")[0].trim()}</h4>
                  <p className="text-[10px] text-gray-400">{svcCategory}</p>
                </div>
              </div>

              <p className="text-xs text-gray-500 mb-3">{t("booking.step2.choose_doc_for_svc")}</p>

              {/* Doctor horizontal list */}
              <div className="flex gap-3 overflow-x-auto pb-2 flex-nowrap">
                {docsToDisplay.map((doc) => {
                  const isSelected = currentDocId === doc.id;
                  const docName = getLocalized(currentLang, doc.name, (doc as any).name_en || (doc as any).nameEn);
                  const docSpecialty = getLocalized(currentLang, doc.specialty, (doc as any).specialty_en || (doc as any).specialtyEn);
                  const docExperience = getLocalized(currentLang, doc.experience, (doc as any).experience_en || (doc as any).experienceEn);

                  return (
                    <button
                      key={doc.id}
                      type="button"
                      onClick={() => handleSelectDoctorForService(index, doc.id)}
                      className={`flex-shrink-0 w-44 p-3 rounded-xl border-2 text-start transition-all duration-200 cursor-pointer flex flex-col gap-2 relative ${
                        isSelected
                          ? "border-brand-forest-500 bg-brand-cream-50"
                          : "border-gray-100 bg-white hover:border-brand-cream-300"
                      }`}
                    >
                      {isSelected && (
                        <div className={`absolute top-2 ${rtl ? "left-2" : "right-2"} w-5 h-5 flex items-center justify-center bg-brand-forest-600 rounded-full`}>
                          <i className="ri-check-line text-white text-[10px]"></i>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <img
                          src={doc.image}
                          alt={docName}
                          className="w-10 h-10 rounded-lg object-cover object-top"
                        />
                        <div className="min-w-0">
                          <p className="font-bold text-xs text-gray-900 truncate">{docName}</p>
                          <p className="text-[10px] text-brand-forest-600 font-medium truncate">{docSpecialty}</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center text-[10px] text-gray-400 mt-1">
                        <span>{docExperience}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex gap-3 mt-6">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 border-2 border-gray-200 text-gray-600 font-semibold px-5 py-3.5 rounded-full hover:border-gray-300 transition-all whitespace-nowrap cursor-pointer"
        >
          <i className={rtl ? "ri-arrow-right-line" : "ri-arrow-left-line"}></i>
          {t("btn.back")}
        </button>
        <button
          onClick={() => canProceed && onNext()}
          disabled={!canProceed}
          className={`inline-flex items-center gap-2 font-bold px-8 py-3.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
            canProceed
              ? "bg-brand-forest-600 hover:bg-brand-forest-700 text-white"
              : "bg-gray-100 text-gray-400 cursor-not-allowed"
          }`}
        >
          {t("booking.step2.continue")}
          <i className={rtl ? "ri-arrow-left-line" : "ri-arrow-right-line"}></i>
        </button>
      </div>
    </div>
  );
}
